import { AlertTriangle, Copy, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { MfaCredentials } from '@/lib/admins-api';

export interface DisplayedCredentials extends MfaCredentials {
  /** Absent après un reset MFA (le mot de passe ne change pas). */
  password?: string;
}

interface CredentialsDialogProps {
  /** Admin concerné : titre de la modal et en-tête du fichier .txt. */
  email: string;
  /** null = modal fermée. Secrets gardés par l'appelant en state local, jamais en cache ni en storage. */
  credentials: DisplayedCredentials | null;
  title: string;
  onClose(): void;
}

export const copyToClipboard = async (text: string, what: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copié`);
  } catch {
    toast.error(`Copie impossible : sélectionnez ${what.toLowerCase()} et copiez-le à la main`);
  }
};

export const backupCodesFileContent = (email: string, codes: string[]): string =>
  [
    `Codes de secours MFA — admin plateforme ${email}`,
    `Générés le ${new Date().toISOString()}. Chaque code ne fonctionne qu'une seule fois.`,
    '',
    ...codes,
    '',
  ].join('\n');

const downloadText = (filename: string, content: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Secret = ({ label, value, testId }: { label: string; value: string; testId: string }) => (
  <div className="space-y-1.5">
    <p className="text-sm font-medium">{label}</p>
    <div className="flex items-center gap-2">
      <code className="flex-1 select-all break-all rounded-md border bg-muted px-3 py-2 font-mono text-sm" data-testid={testId}>
        {value}
      </code>
      <Button type="button" variant="outline" size="sm" onClick={() => void copyToClipboard(value, label)} aria-label={`Copier : ${label}`}>
        <Copy className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  </div>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-3 rounded-lg border p-4">
    <h3 className="text-sm font-semibold">{title}</h3>
    {children}
  </section>
);

/**
 * Identifiants affichés une seule fois (création d'admin, reset MFA). Bloquante : ni Échap, ni clic à l'extérieur,
 * ni croix ; « Fermer » n'est actif qu'une fois la case cochée. Un rechargement de page demande confirmation.
 */
export const CredentialsDialog = ({ email, credentials, title, onClose }: CredentialsDialogProps) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const open = credentials !== null;

  useEffect(() => {
    if (!open) return;
    setAcknowledged(false);
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Requis par Chrome < 119 pour afficher la confirmation
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [open]);

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        className="max-h-[92vh] max-w-2xl overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {credentials && (
          <>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                Identifiants de <span className="font-semibold text-foreground">{email}</span>, à transmettre par un canal sûr.
              </DialogDescription>
            </DialogHeader>

            <div role="alert" className="flex gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm font-medium text-danger">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Ces informations ne seront affichées qu'une seule fois. Elles ne sont pas récupérables après fermeture.
            </div>

            {credentials.password && (
              <Section title="Mot de passe initial">
                <Secret label="Mot de passe" value={credentials.password} testId="credentials-password" />
                <p className="text-xs text-muted-foreground">L'admin devra le changer à sa première connexion.</p>
              </Section>
            )}

            <Section title="Application d'authentification (TOTP)">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="mx-auto shrink-0 rounded-md border bg-white p-2 sm:mx-0" data-testid="credentials-qr">
                  <QRCodeSVG value={credentials.mfaQrCode} size={176} level="M" title="QR code TOTP" />
                </div>
                <div className="min-w-0 flex-1 space-y-3">
                  <p className="text-xs text-muted-foreground">
                    À scanner avec Google Authenticator, Aegis, 1Password… Sinon, saisir le secret à la main.
                  </p>
                  <Secret label="Secret" value={credentials.mfaSecret} testId="credentials-secret" />
                  <Button type="button" variant="outline" size="sm" onClick={() => void copyToClipboard(credentials.mfaQrCode, 'Lien otpauth')}>
                    <Copy className="mr-2 h-4 w-4" aria-hidden />
                    Copier le lien otpauth
                  </Button>
                </div>
              </div>
            </Section>

            <Section title={`Codes de secours (${credentials.backupCodes.length})`}>
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="credentials-backup-codes">
                {credentials.backupCodes.map((code) => (
                  <li key={code} className="rounded-md border bg-muted px-2 py-1.5 text-center font-mono text-sm">
                    {code}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void copyToClipboard(credentials.backupCodes.join('\n'), 'Codes de secours')}
                >
                  <Copy className="mr-2 h-4 w-4" aria-hidden />
                  Copier les codes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadText(`codes-secours-${email}.txt`, backupCodesFileContent(email, credentials.backupCodes))}
                >
                  <Download className="mr-2 h-4 w-4" aria-hidden />
                  Télécharger (.txt)
                </Button>
              </div>
            </Section>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-brand-violet"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              J'ai noté ou transmis ces informations
            </label>

            <DialogFooter>
              <Button type="button" disabled={!acknowledged} onClick={onClose}>
                Fermer
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
